import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageCircle, ArrowRight, Calendar, Eye, Clock, Search, ChevronRight, Sparkles } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

// Blog Card Component - Simplified
function BlogCard({ post }) {
  const dateStr = post.published_at 
    ? new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) 
    : 'Recent';

  return (
    <Link to={"/blog/" + post.slug} className="block group">
      <div className="bg-white rounded-xl border border-gray-200 p-6 h-full hover:shadow-lg transition-shadow">
        <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
          <Calendar size={14} />
          <span>{dateStr}</span>
          <span className="mx-2">•</span>
          <Eye size={14} />
          <span>{post.views || 0}</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#2C39B8] transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-3 mb-4">{post.summary}</p>
        <div className="flex items-center text-[#2C39B8] text-sm font-medium">
          Read more <ChevronRight size={16} />
        </div>
      </div>
    </Link>
  );
}

// Blog Listing Page
export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    async function fetchPosts() {
      try {
        const res = await fetch(API_URL + '/api/seo/blog?limit=50');
        const data = await res.json();
        setPosts(data.posts || []);
        setTotal(data.total || 0);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, []);

  const filteredPosts = posts.filter(function(post) {
    const query = searchQuery.toLowerCase();
    const titleMatch = post.title && post.title.toLowerCase().includes(query);
    const summaryMatch = post.summary && post.summary.toLowerCase().includes(query);
    return titleMatch || summaryMatch;
  });

  return (
    <div data-testid="blog-list-page">
      <Navbar />
      <SEOHead title="Blog - B2B Loyalty Insights, Trade Marketing & Channel Strategy" description="Expert insights on B2B channel loyalty program design, trade marketing automation, dealer engagement strategies, and channel incentive best practices from ChannelLoyalty." keywords="B2B loyalty blog, dealer loyalty insights, channel incentive strategy, trade marketing articles" canonical="https://channelloyalty.ai/blog" />
      
      <main>
        <section className="hero-full" style={{ paddingBottom: '60px' }}>
          <div className="container">
            <div className="text-center pt-8 max-w-4xl mx-auto">
              <div className="hero-badge mx-auto mb-6">
                <Sparkles size={16} />
                Insights & Resources
              </div>
              <h1 className="text-white mb-6">ChannelLoyalty Blog</h1>
              <p className="text-white/80 text-lg mb-8">
                Expert insights on B2B loyalty, channel incentives, and trade rewards.
              </p>
              
              <div className="max-w-md mx-auto relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={function(e) { setSearchQuery(e.target.value); }}
                  className="w-full pl-12 pr-4 py-3 rounded-xl border-0 bg-white/10 text-white placeholder-white/50 focus:bg-white/20 focus:outline-none"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            {loading ? (
              <div className="text-center py-20">
                <div className="animate-spin w-8 h-8 border-4 border-[#2C39B8] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500">Loading articles...</p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-20">
                <Sparkles size={48} className="text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Coming Soon</h3>
                <p className="text-gray-500">Our blog content is being generated. Check back shortly!</p>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  All Articles <span className="text-gray-400 font-normal">({total})</span>
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredPosts.map(function(post) {
                    return <BlogCard key={post.slug} post={post} />;
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}

// Single Blog Post Page
export function BlogPostPage() {
  const params = useParams();
  const slug = params.slug;
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPost() {
      try {
        const res = await fetch(API_URL + '/api/seo/blog/' + slug);
        if (res.ok) {
          const data = await res.json();
          setPost(data);
        }
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="blog-post-page">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#2C39B8] border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div data-testid="blog-post-page">
        <SEOHead title="Article Not Found" description="This article doesn't exist. Explore our B2B loyalty insights blog." noindex={true} />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
            <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
          </div>
        </div>
        <MegaFooter />
      </div>
    );
  }

  const dateStr = post.published_at 
    ? new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) 
    : '';

  return (
    <div data-testid="blog-post-page">
      <SEOHead
        title={post.title}
        description={post.meta_description || post.summary}
        keywords={post.keywords ? (Array.isArray(post.keywords) ? post.keywords.join(', ') : post.keywords) : ''}
        canonical={'https://channelloyalty.ai/blog/' + slug}
        ogType="article"
        article={{ datePublished: post.published_at, dateModified: post.published_at }}
      />
      <Navbar />
      
      <main>
        <section className="hero-full" style={{ paddingBottom: '40px' }}>
          <div className="container">
            <div className="max-w-4xl mx-auto pt-8">
              <Link to="/blog" className="inline-flex items-center text-white/70 hover:text-white mb-6">
                <ChevronRight size={16} className="rotate-180 mr-1" /> Back to Blog
              </Link>
              <h1 className="text-white text-4xl md:text-5xl mb-6">{post.title}</h1>
              <div className="flex items-center gap-4 text-white/70">
                <span className="flex items-center gap-2">
                  <Calendar size={16} />
                  {dateStr}
                </span>
                <span className="flex items-center gap-2">
                  <Eye size={16} />
                  {post.views || 0} views
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <article className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </article>

              <div className="mt-12 bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] rounded-2xl p-8 text-center text-white">
                <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Channel Loyalty?</h3>
                <p className="text-white/80 mb-6">See how ChannelLoyalty can help you build world-class loyalty programs.</p>
                <Link to="/contact" className="btn btn-primary bg-white text-[#2C39B8] hover:bg-gray-100">
                  Request Demo <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}

export default BlogListPage;
